import { ref, shallowRef } from 'vue'

import type { RuntimeConfig } from '@/config'
import { describeError, logger } from '@/lib/log'
import {
  ManifestMissingError,
  normalizePlaylist,
  parseManifestResponse,
  resolveMediaUrl,
  type NormalizeDefaults,
} from '@/lib/playlist'
import type { Playlist } from '@/types'

const FETCH_TIMEOUT_MS = 15_000
const BACKOFF_START_MS = 5_000
const FALLBACK_PLAYLIST = 'default'

/** Safety net only — the build script writes explicit values into the manifest. */
const NORMALIZE_DEFAULTS: NormalizeDefaults = {
  imageDurationMs: 10_000,
  transitionMs: 600,
  fit: 'cover',
}

function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)
  return fetch(url, { cache: 'no-store', credentials: 'omit', signal: controller.signal }).finally(
    () => window.clearTimeout(timer),
  )
}

/**
 * Keeps the playlist manifest fresh without a backend: the manifest is polled and
 * the `playlist` ref is only replaced when its revision actually changes, so
 * consumers can simply `watch` it. A failed poll never disturbs playback — the
 * last known good playlist keeps running.
 */
export function usePlaylist(config: RuntimeConfig) {
  const playlist = shallowRef<Playlist | null>(null)
  const problems = ref<string[]>([])
  const error = ref<string | null>(null)
  const lastLoadedAt = ref(0)
  const consecutiveFailures = ref(0)

  let running = false
  let pollTimer = 0

  async function fetchManifest(name: string): Promise<unknown> {
    const url = `${config.mediaBase}/playlists/${encodeURIComponent(name)}.json?r=${Date.now()}`
    const response = await fetchWithTimeout(url, FETCH_TIMEOUT_MS)
    return parseManifestResponse(response.status, await response.text(), url)
  }

  async function load(): Promise<void> {
    let name = config.playlistName
    let raw: unknown
    try {
      raw = await fetchManifest(name)
    } catch (caught) {
      if (caught instanceof ManifestMissingError && name !== FALLBACK_PLAYLIST) {
        logger.warn(`Playlist "${name}" not found, falling back to "${FALLBACK_PLAYLIST}"`)
        name = FALLBACK_PLAYLIST
        raw = await fetchManifest(name)
      } else {
        throw caught
      }
    }

    const result = normalizePlaylist(raw, name, NORMALIZE_DEFAULTS)
    problems.value = result.problems
    for (const problem of result.problems) logger.warn(`Manifest: ${problem}`)

    if (result.playlist.items.length === 0) {
      throw new Error(`Playlist "${name}" contains no usable items`)
    }

    lastLoadedAt.value = Date.now()
    if (playlist.value?.revision === result.playlist.revision) return

    playlist.value = {
      ...result.playlist,
      items: result.playlist.items.map((item) => ({
        ...item,
        src: resolveMediaUrl(config.mediaBase, item.src),
        poster: item.poster ? resolveMediaUrl(config.mediaBase, item.poster) : null,
      })),
    }
    logger.info(
      `Loaded playlist "${result.playlist.name}" rev ${result.playlist.revision} — ` +
        `${result.playlist.items.length} item(s), ${Math.round(result.playlist.cycleMs / 1_000)}s cycle`,
    )
  }

  function schedulePoll(delayMs: number): void {
    window.clearTimeout(pollTimer)
    if (!running) return
    pollTimer = window.setTimeout(() => {
      void poll()
    }, delayMs)
  }

  async function poll(): Promise<void> {
    if (!running) return
    try {
      await load()
      error.value = null
      consecutiveFailures.value = 0
      schedulePoll(config.playlistPollMs)
    } catch (caught) {
      consecutiveFailures.value += 1
      error.value = describeError(caught)
      logger.warn(`Playlist poll failed (${consecutiveFailures.value}): ${error.value}`)
      const backoff = Math.min(
        config.playlistPollMs,
        BACKOFF_START_MS * 2 ** Math.min(consecutiveFailures.value - 1, 4),
      )
      schedulePoll(backoff)
    }
  }

  function onOnline(): void {
    logger.info('Network came back — refreshing the playlist')
    void poll()
  }

  function start(): void {
    if (running) return
    running = true
    window.addEventListener('online', onOnline)
    void poll()
  }

  function stop(): void {
    running = false
    window.clearTimeout(pollTimer)
    window.removeEventListener('online', onOnline)
  }

  return {
    playlist,
    problems,
    error,
    lastLoadedAt,
    consecutiveFailures,
    start,
    stop,
    reload: poll,
  }
}
